import { useMemo, useState } from "react";
import { useFormikContext } from "formik";
import { Paper, Stack, Typography, Button, useTheme } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { WebValidationIcon, Link02Icon } from "@hugeicons/core-free-icons";
import SortableList from "@/components/SortableList";
import { EmptyState } from "@/components";

import LinkDialog from "./LinkDialog";

interface Link {
  title: string;
  href: string;
  target: "_self" | "_blank";
}

const QuickLinksSection = () => {
  const { values, setFieldValue } = useFormikContext<any>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const theme = useTheme();

  // Dynamically find the links key in settings (type is 'link')
  const linksKey = useMemo(() => {
    return (
      Object.keys(values.settings ?? {}).find((key) => values.settings[key].type === "link") ||
      "links"
    );
  }, [values.settings]);

  const linksSetting = values.settings?.[linksKey];
  const links: Link[] = useMemo(() => {
    return linksSetting?.value ?? [];
  }, [linksSetting?.value]);

  const sortableItems = useMemo(() => {
    return links.map((link, index) => ({
      ...link,
      id: `${link.title}-${index}`,
      label: link.title,
    }));
  }, [links]);

  const handleSaveLink = (link: Link) => {
    const setting = values.settings?.[linksKey] ?? {};
    const updatedLinks = [...links];

    if (editingIndex !== null) {
      updatedLinks[editingIndex] = link;
    } else {
      updatedLinks.push(link);
    }

    setFieldValue(`settings.${linksKey}`, { ...setting, value: updatedLinks });
  };

  const handleSortChange = (newItems: any[]) => {
    const setting = values.settings?.[linksKey] ?? {};
    const updatedLinks = newItems.map(({ id, label, ...link }) => link);
    setFieldValue(`settings.${linksKey}`, { ...setting, value: updatedLinks });
  };

  const handleSortEdit = (item: any) => {
    const index = links.findIndex((l, i) => `${l.title}-${i}` === item.id);
    if (index !== -1) {
      setEditingIndex(index);
      setDialogOpen(true);
    }
  };

  const handleSortDelete = (newItems: any[]) => {
    const setting = values.settings?.[linksKey] ?? {};
    const updatedLinks = newItems.map(({ id, label, ...link }) => link);
    setFieldValue(`settings.${linksKey}`, { ...setting, value: updatedLinks });
  };

  const handleOpenAdd = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const currentEditingLink = useMemo(() => {
    if (editingIndex !== null && links[editingIndex]) {
      return links[editingIndex];
    }
    return undefined;
  }, [editingIndex, links]);

  const renderSortableLink = (item: any) => (
    <Stack spacing={0.5} sx={{ py: 1 }}>
      <Typography variant="body1" fontWeight={500}>
        {item.title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {item.target === "_blank" ? "Externo: " : ""}
        {item.href}
      </Typography>
    </Stack>
  );

  return (
    <Paper sx={{ p: 3, borderRadius: 3 }}>
      <Stack spacing={3}>
        {/* Header Row */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={2} alignItems="center">
            <HugeiconsIcon icon={WebValidationIcon} size={32} color={theme.palette.primary.main} />
            <Typography variant="h3">Enlaces Rápidos</Typography>
          </Stack>
          <Button variant="contained" color="primary" onClick={handleOpenAdd}>
            Agregar enlace
          </Button>
        </Stack>

        {/* Links List using SortableList */}
        <Stack spacing={1.5}>
          {links.length === 0 ? (
            <EmptyState
              title="Sin Enlaces"
              description="No hay enlaces agregados aún. Agrega enlaces a redes sociales, menús externos o tu sitio web principal."
              icon={Link02Icon}
            />
          ) : (
            <SortableList
              items={sortableItems}
              onChange={handleSortChange}
              onEdit={handleSortEdit}
              onDelete={handleSortDelete}
              renderItem={renderSortableLink}
            />
          )}
        </Stack>
      </Stack>

      <LinkDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSaveLink}
        initialValue={currentEditingLink}
      />
    </Paper>
  );
};

export default QuickLinksSection;
